/**
 * Peta jaring radius: siapa yang masuk lingkaran, dan siapa yang hampir.
 *
 * Tabel jaring sudah menyebut tiap sumur berikut jaraknya, dan untuk
 * "berapa titik yang disumbang KAG-014" tabel itu jawaban yang lengkap. Yang
 * tidak bisa dijawabnya adalah pertanyaan yang justru paling sering ditanyakan
 * sebelum radiusnya disetel: **bentuk** dari yang terjaring. Sebuah daftar
 * berisi enam nama dan enam jarak tidak memperlihatkan bahwa keenamnya duduk
 * di satu sisi lingkaran, sedangkan sisi seberangnya kosong — dan Rw yang
 * dibaca dari setengah lingkaran adalah Rw dari setengah akuifer. Tidak juga
 * memperlihatkan bahwa sumur ketujuh berdiri seratus meter di luar garis,
 * yaitu keterangan yang mengubah radius seseorang dari 5 km menjadi 5,2 km.
 *
 * Maka yang digambar bukan hanya yang lolos. Sumur yang tidak lolos tetap ada
 * di peta, diredupkan, sebab peta yang hanya memuat yang lolos memperlihatkan
 * sebuah lingkaran yang selalu terisi rapi dan tidak pernah bisa mengatakan
 * bahwa radiusnya kesempitan.
 *
 * Warnanya dipinjam dari `SCREEN_TONE`, yaitu warna yang dipakai tabel di
 * sebelahnya. Peta yang mengeja "inside" dengan hijau sementara tabelnya
 * mengeja dengan biru adalah dua keterangan tentang satu sumur yang harus
 * diterjemahkan orang di kepalanya sendiri.
 *
 * Yang menentukan warna adalah **hasil penjaringan dari server**, bukan
 * pengukuran ulang jarak di sini. Sebuah peta yang memutuskan sendiri siapa
 * yang di dalam lingkaran cepat atau lambat akan berselisih dengan angka yang
 * dipakai menghitung Rw — terutama pada mode `trajectory`, tempat yang
 * menentukan bukan jarak antar kepala sumur melainkan jarak pada tiap
 * kedalaman.
 *
 * Tiga hal menjawab pertanyaan yang datang **setelah** bentuknya terbaca, dan
 * ketiganya menunjuk satu titik tertentu alih-alih seluruh jaring. Nama sumur
 * dipasang menurut ruang yang tersisa di layar, sehingga memperbesar
 * memulangkannya satu per satu. Kisi koordinat menyebut tempat di tepinya,
 * dengan langkah yang dihitung ulang dari bingkai yang sedang tampak — angka
 * yang berubah sendiri ketika peta diperbesar. Dan sumur yang ditunjuk kursor
 * menyala penuh serta membawa barisnya sendiri dari tabel jaring — keadaan,
 * jarak dari pusat, dan koordinatnya — sebab jawaban itu selama ini harus
 * dicari di baris keberapa pun ia duduk di antara dua ratus baris.
 */

import { useCallback, useMemo, useState } from "react";

import { useElementSize, useMapView } from "../hooks";
import {
  applyView, extentOf, fitTransform,
  type Extent, type MapTransform,
} from "../lib/basemap";
import { coordinateStep, coordinateTicks } from "../lib/ticks";
import type { ScreenState, Screening, ScreenedWell } from "../types";
import { SCREEN_TONE } from "./DataScope";
import MapZoomControls from "./MapZoomControls";
import { Empty, Segmented } from "./ui";

/** Sumur sebagaimana dibutuhkan peta ini: sebuah nama dan sebuah tempat. */
export interface ScreeningMapWell {
  id: number;
  name: string;
  x: number | null;
  y: number | null;
}

/** Keadaan yang berarti sumurnya ikut dibaca. */
const KEPT: ScreenState[] = ["centre", "inside", "partly inside"];

/** Kotak yang memuat keduanya. */
function union(a: Extent, b: Extent): Extent {
  return {
    min_x: Math.min(a.min_x, b.min_x), max_x: Math.max(a.max_x, b.max_x),
    min_y: Math.min(a.min_y, b.min_y), max_y: Math.max(a.max_y, b.max_y),
  };
}

/** Kotak yang sama, dilebarkan sepersekian rentangnya di tiap sisi. */
function grow(extent: Extent, fraction: number): Extent {
  const padX = (extent.max_x - extent.min_x) * fraction;
  const padY = (extent.max_y - extent.min_y) * fraction;
  return {
    min_x: extent.min_x - padX, max_x: extent.max_x + padX,
    min_y: extent.min_y - padY, max_y: extent.max_y + padY,
  };
}

/** Jari-jari sebagaimana dibaca orang: "5 km", "750 m". */
export function radiusText(metres: number): string {
  return metres >= 1000
    ? `${(metres / 1000).toFixed(2).replace(/\.?0+$/, "")} km`
    : `${Math.round(metres).toLocaleString("en-US")} m`;
}

/**
 * Sumur sebagaimana dibacakan ketika tetikus berhenti di atasnya.
 *
 * Isinya sama dengan keterangan yang mengapung di sampingnya, dan itu memang
 * disengaja: yang satu untuk mata, yang satu untuk tooltip bawaan peramban dan
 * pembaca layar. Sebuah sorotan yang hanya digambar adalah sorotan yang tidak
 * pernah sampai kepada yang tidak melihat petanya.
 */
function wellTitle(
  name: string,
  tone: ScreenState,
  row: ScreenedWell | undefined,
  centre: string,
): string {
  const head = `${name} — ${tone}`;
  return row?.distance_m != null
    ? `${head}, ${radiusText(row.distance_m)} from ${centre}`
    : head;
}

/**
 * Kisi koordinat survei berikut angkanya, menempel pada tanah.
 *
 * Peta ini sebelumnya adalah bidang kosong berisi titik: ia mengatakan bentuk
 * — siapa mengelilingi siapa — tetapi tidak satu pun tempat. Yang hilang
 * bersamanya adalah jembatan ke seluruh keterangan lapangan yang lain, sebab
 * sumur di Prabumulih tidak disebut orang "yang di kiri atas" melainkan
 * "yang di 512.000 mT", dan peta tanpa angka menuntut pembacanya menghafal
 * bentuk alih-alih membaca letak.
 *
 * Langkahnya dihitung ulang dari **bingkai yang sedang tampak**, bukan dari
 * bingkai pasnya: itulah sebabnya angkanya berubah sendiri ketika peta
 * diperbesar. Lingkaran 2 km yang memenuhi panel mendapat garis tiap 500 m;
 * lapangan 80 km mendapat garis tiap 20 km — kisi yang cacah garisnya tetap
 * sedangkan jaraknya mengikuti apa yang muat.
 *
 * Satu langkah untuk kedua sumbu, sebab petanya setara skala: kisi persegi
 * adalah satu-satunya kisi yang jaraknya boleh dibaca sebagai jarak.
 */
function Graticule({
  transform,
  width,
  height,
}: {
  transform: MapTransform;
  width: number;
  height: number;
}) {
  if (!(width > 0) || !(height > 0)) return null;

  const [west, north] = transform.toWorld(0, 0);
  const [east, south] = transform.toWorld(width, height);
  if (!Number.isFinite(west) || !Number.isFinite(south)) return null;

  // Tiga pembagian dan bukan enam. Panel ini duduk di separuh lebar sebuah
  // panel yang dibagi dua dengan tabel jaringnya — tiga ratus piksel pada
  // layar biasa — dan sebuah northing tujuh angka memakan empat puluh piksel
  // di antaranya.
  const step = coordinateStep(Math.min(east - west, north - south), 3);

  return (
    <g pointerEvents="none" aria-hidden="true">
      {coordinateTicks(west, east, step).map((value) => {
        const x = transform.toScreen(value, north)[0];
        return (
          <g key={`e${value}`} data-axis="easting">
            <line x1={x} x2={x} y1={0} y2={height}
                  stroke="var(--grid-line)" strokeWidth={1} />
            {/* Angka yang separuhnya terpotong tepi panel terbaca sebagai
                angka yang lain — "512.000" yang kehilangan digit pertamanya
                adalah "12.000", dan itu bukan tempat mana pun. */}
            {x > 26 && x < width - 26 && (
              <text x={x} y={height - 4} textAnchor="middle" style={tickLabel}>
                {value.toFixed(0)}
              </text>
            )}
          </g>
        );
      })}
      {coordinateTicks(south, north, step).map((value) => {
        const y = transform.toScreen(east, value)[1];
        return (
          <g key={`n${value}`} data-axis="northing">
            <line x1={0} x2={width} y1={y} y2={y}
                  stroke="var(--grid-line)" strokeWidth={1} />
            {/* Baris paling bawah disisakan untuk easting-nya. */}
            {y > 12 && y < height - 16 && (
              <text x={4} y={y - 3} style={tickLabel}>{value.toFixed(0)}</text>
            )}
          </g>
        );
      })}
    </g>
  );
}

export default function ScreeningMap({
  screening,
  wells,
  height = 280,
}: {
  screening: Screening;
  /** Seluruh sumur lapangan, bukan hanya yang diminta: yang di luar jaring
   *  adalah separuh dari apa yang dikatakan peta ini. */
  wells: ScreeningMapWell[];
  height?: number;
}) {
  const [box, size] = useElementSize<HTMLDivElement>();
  /**
   * Zoom dan geseran, dari kail yang sama dengan ketiga peta lain.
   *
   * Sebuah lapangan 1.451 sumur yang dijaring pada radius seratus kilometer
   * menggambar gerombolan selebar dua belas piksel: keempat puluh sumur yang
   * sedang ditanyakan orang bertumpuk di satu titik, dan tidak ada cara
   * membacanya tanpa mengubah radiusnya — yaitu mengubah pertanyaannya demi
   * bisa melihat jawabannya. Roda memperbesar di sekitar kursor dan seretan
   * menggeser, persis seperti di peta lapangan, basemap korelasi, dan model
   * geologi: tiga peta yang sudah mengajari tangan orang cara memakainya.
   */
  const zoom = useMapView();
  // Satu ref gabungan yang tetap identitasnya: ref sebaris melahirkan fungsi
  // baru tiap render, dan React akan melepas lalu memasang ulang pengamat
  // ukuran serta seluruh pendengar peta pada setiap render.
  const attachMap = useCallback(
    (el: HTMLDivElement | null) => {
      box(el);
      zoom.bind(el);
    },
    [box, zoom.bind],
  );
  /**
   * Yang dibingkai: sekitar lingkarannya, atau seluruh lapangan.
   *
   * Bawaannya lingkaran, dan itu bukan selera. Prabumulih selebar empat puluh
   * kilometer: sebuah lingkaran berjari-jari 2 km yang digambar pada bingkai
   * selebar itu adalah cakram selebar dua belas piksel, dan tidak ada satu pun
   * pertanyaan tentang radius yang bisa dijawab olehnya. Seluruh lapangan
   * tetap semeter jauhnya, sebab ia menjawab pertanyaan yang lain — di sebelah
   * mana lapangan ini lingkarannya berdiri.
   */
  const [frame, setFrame] = useState<"circle" | "field">("circle");
  /**
   * Sumur yang sedang ditunjuk kursor.
   *
   * Peta yang penuh menjawab pertanyaan "bentuknya bagaimana" dan berhenti di
   * situ: pertanyaan berikutnya selalu tentang satu titik tertentu — yang
   * berdiri sendirian di seberang lingkaran, atau yang redup tepat di luar
   * garis — dan jawabannya ada di tabel sebelah, di baris keberapa pun ia
   * duduk di antara dua ratus baris. Menunjuknya dengan kursor memulangkan
   * jawaban itu ke tempat pertanyaannya lahir.
   */
  const [hovered, setHovered] = useState<number | null>(null);

  const rows = useMemo(
    () => new Map(screening.wells.map((row) => [row.well_id, row])),
    [screening],
  );
  const state = useMemo(
    () => new Map(screening.wells.map((row) => [row.well_id, row.state])),
    [screening],
  );
  const located = useMemo(
    () => wells.filter((w) => w.x !== null && w.y !== null),
    [wells],
  );
  const centre = located.find((w) => w.id === screening.center_well) ?? null;

  const bounds = useMemo<Extent | null>(() => {
    const field = extentOf(located);
    const r = screening.radius_m;
    if (frame === "field" || centre === null) {
      // Lingkarannya ikut dibingkai juga di sini. Sebuah radius yang lebih
      // lebar daripada lapangannya sendiri akan terpotong di tepi panel, dan
      // yang ikut hilang bersama potongannya adalah label jari-jarinya —
      // sehingga bingkai "seluruh lapangan" justru kehilangan satu-satunya
      // keterangan tentang seberapa jauh lingkaran itu menjangkau.
      if (!field || !centre) return field;
      // Dilebarkan sedikit: label jari-jari duduk lima piksel di atas garis
      // lingkarannya, dan kotak yang pas benar memotong tulisannya.
      return grow(union(field, {
        min_x: centre.x! - r, max_x: centre.x! + r,
        min_y: centre.y! - r, max_y: centre.y! + r,
      }), 0.04);
    }
    // Lingkarannya, lalu tiap sumur yang lolos. Keduanya, sebab pada mode
    // `trajectory` sebuah tetangga bisa terjaring di kedalaman sementara
    // kepalanya berdiri di luar garis — dan sumur yang diwarnai "inside" di
    // luar bingkai adalah warna yang tidak bisa dicari siapa pun.
    let want: Extent = {
      min_x: centre.x! - r, max_x: centre.x! + r,
      min_y: centre.y! - r, max_y: centre.y! + r,
    };
    for (const well of located) {
      if (!KEPT.includes(state.get(well.id) ?? "outside radius")) continue;
      want = union(want, { min_x: well.x!, max_x: well.x!,
                           min_y: well.y!, max_y: well.y! });
    }
    return grow(want, 0.16);
  }, [frame, located, centre, screening.radius_m, state]);

  const fitted = useMemo(
    () => fitTransform(bounds, size.width, height, 16),
    [bounds, size.width, height],
  );

  // Pandangan pengguna duduk di atas peta yang sudah pas, bukan
  // menggantikannya: bingkainya tetap ditentukan lingkaran dan sumurnya, jadi
  // memperbesar tidak mengubah apa yang dianggap "seluruh jaring ini".
  const transform = useMemo(() => applyView(fitted, zoom.view), [fitted, zoom.view]);

  /** Sumur yang benar-benar jatuh di dalam bingkainya, dan yang tidak. */
  const { drawn, beyond } = useMemo(() => {
    if (!transform) return { drawn: [], beyond: 0 };
    const rows: { well: ScreeningMapWell; at: [number, number];
                  tone: ScreenState }[] = [];
    let missed = 0;
    for (const well of located) {
      const at = transform.toScreen(well.x!, well.y!) as [number, number];
      if (at[0] < -2 || at[0] > size.width + 2 || at[1] < -2 || at[1] > height + 2) {
        missed += 1;
        continue;
      }
      rows.push({ well, at, tone: state.get(well.id) ?? "outside radius" });
    }
    return { drawn: rows, beyond: missed };
  }, [transform, located, state, size.width, height]);

  /** Sumur yang tidak bisa digambar sama sekali — tanpa koordinat. */
  const unplaced = wells.length - located.length;

  const ring = centre && transform
    ? {
        at: transform.toScreen(centre.x!, centre.y!) as [number, number],
        r: screening.radius_m * transform.scale,
      }
    : null;

  /**
   * Nama siapa yang muat tanpa menimpa nama tetangganya.
   *
   * Yang menentukan keterbacaan adalah jarak antar tulisan di layar, bukan
   * cacah sumurnya — dan bedanya bukan teori. Gerombolan Arahan di Prabumulih
   * memuat 33 sumur dalam jarak dua ratus meter: sebuah aturan berdasarkan
   * cacah ("beri nama kalau yang lolos di bawah dua belas") tidak pernah
   * menyalakan satu nama pun di sana, bahkan pada perbesaran lima kali,
   * padahal pada perbesaran itu titiknya sudah terpisah seratus piksel dan
   * namanya justru sudah muat. Zoom yang tidak pernah memulangkan satu nama
   * adalah zoom ke dalam simpul yang tetap tak terbaca.
   *
   * Jadi namanya dipasang serakah: yang terjaring lebih dulu, pusatnya paling
   * dulu, dan sebuah nama dilewati ketika kotaknya bertabrakan dengan nama
   * yang sudah berdiri. Memperbesar merenggangkan titiknya, dan nama muncul
   * sendiri satu per satu — tanpa sakelar yang harus ditemukan lebih dulu.
   *
   * Yang tidak terjaring ikut bernama, di urutan paling belakang. Sumur redup
   * ada di peta ini justru untuk mengatakan bahwa radiusnya kesempitan, dan
   * "ada satu tetangga tepat di luar garis" adalah setengah keterangan sampai
   * ia bernama: yang menggeser radius dari 5 km ke 5,2 km perlu tahu bahwa
   * yang ditunggunya di situ adalah KAG-021. Namanya tetap kalah dari nama
   * yang terjaring ketika keduanya berebut satu tempat.
   */
  const labelled = useMemo(() => {
    const placed: [number, number][] = [];
    const out = new Set<number>();
    const rank = (tone: ScreenState) =>
      tone === "centre" ? 0 : KEPT.includes(tone) ? 1 : 2;
    const urut = [...drawn].sort((a, b) => rank(a.tone) - rank(b.tone));
    for (const row of urut) {
      // Batas atas yang murah: enam puluh nama sudah lebih banyak daripada
      // yang dibaca siapa pun sekali pandang, dan yang di atasnya hanya
      // menambah simpul DOM di dalam panel yang tingginya tetap.
      if (out.size >= 60) break;
      const [x, y] = row.at;
      // Kotak, bukan lingkaran: tulisannya panjang dan pendek, jadi dua nama
      // yang bersebelahan mendatar bertabrakan jauh sebelum dua nama yang
      // bersusun tegak.
      if (placed.some(([px, py]) => Math.abs(px - x) < 54 && Math.abs(py - y) < 11)) {
        continue;
      }
      placed.push([x, y]);
      out.add(row.well.id);
    }
    return out;
  }, [drawn]);

  /**
   * Sumur yang sedang ditunjuk, berikut tempatnya di layar dan barisnya di
   * tabel jaring. Dicari di antara yang **tergambar**: sumur yang keluar dari
   * bingkai karena peta digeser tidak boleh meninggalkan keterangan yang
   * menggantung di sudut panel tentang titik yang tidak ada lagi.
   */
  const hot = useMemo(() => {
    const row = drawn.find((item) => item.well.id === hovered);
    return row ? { ...row, screened: rows.get(row.well.id) ?? null } : null;
  }, [drawn, hovered, rows]);

  const legend = (["centre", "inside", "partly inside", "outside radius",
                   "no coordinates"] as ScreenState[])
    .filter((tone) => screening.wells.some((row) => row.state === tone));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8,
                    justifyContent: "space-between", flexWrap: "wrap" }}>
        <span style={label}>
          Wells caught — {radiusText(screening.radius_m)} around{" "}
          {screening.center_name}
        </span>
        <Segmented
          value={frame}
          size="sm"
          options={[
            { value: "circle", label: "Around the circle" },
            { value: "field", label: "Whole field" },
          ]}
          onChange={(next) => {
            // Pandangan dipulangkan bersama bingkainya. Perbesaran tiga kali
            // yang diukur terhadap lingkaran 2 km menunjuk tempat yang sama
            // sekali lain ketika bingkainya berganti menjadi lapangan
            // selebar delapan puluh kilometer, dan yang menekan sakelar ini
            // meminta pemandangan yang lain — bukan tempat yang sama pada
            // skala yang lain.
            zoom.reset();
            setFrame(next as "circle" | "field");
          }}
        />
      </div>

      <div
        ref={attachMap}
        // Kursor yang meninggalkan kotaknya meninggalkan sorotannya juga:
        // `pointerleave` sebuah sumur tidak selalu terjadi ketika tangan
        // bergerak cepat ke luar panel, dan sorotan yang tertinggal menunjuk
        // sumur yang sudah tidak ditunjuk siapa pun.
        onPointerLeave={() => setHovered(null)}
        style={{
          position: "relative", height, minWidth: 0,
          border: "1px solid var(--line-strong)",
          borderRadius: "var(--radius-panel)",
          background: "var(--panel)", overflow: "hidden",
          cursor: zoom.panning ? "grabbing" : undefined,
          touchAction: "none",
        }}
      >
        {!located.length ? (
          <Empty>No well of this field carries coordinates yet.</Empty>
        ) : (
          <svg
            width="100%"
            height={height}
            aria-label="Screened wells basemap"
            style={{ display: "block" }}
          >
            {/* Paling dulu digambar, jadi lingkaran dan sumurnya berdiri di
                atasnya — dan bukan sebaliknya. */}
            {transform && (
              <Graticule transform={transform} width={size.width} height={height} />
            )}

            {ring && (
              <>
                {/* Lingkarannya di bawah sumurnya: garis yang melintas di atas
                    sebuah titik menyembunyikan justru titik yang sedang
                    ditanyakan apakah ia di dalam atau di luar. */}
                <circle
                  cx={ring.at[0]} cy={ring.at[1]} r={ring.r}
                  fill="var(--accent)" fillOpacity={0.06}
                  stroke="var(--accent)" strokeWidth={1.2}
                  // Putus-putus pada mode lintasan, sebab yang digambar di sini
                  // adalah lingkaran di kepala sumur sedangkan yang menjaring
                  // adalah lingkaran yang menuruni lintasannya: pada sumur
                  // miring keduanya tidak sama, dan garis utuh akan
                  // menjanjikan ketepatan yang tidak dimilikinya.
                  strokeDasharray={screening.mode === "trajectory" ? "4 3" : undefined}
                />
                <text
                  x={ring.at[0]} y={ring.at[1] - ring.r - 5}
                  textAnchor="middle" style={ringLabel}
                >
                  {radiusText(screening.radius_m)}
                </text>
              </>
            )}

            {drawn.map(({ well, at, tone }) => {
              const kept = KEPT.includes(tone);
              const lit = hovered === well.id;
              return (
                <g key={well.id} data-well={well.id}
                   // Sumur redup yang ditunjuk menyala penuh: yang sedang
                   // ditanyakan orang tidak boleh menjadi yang paling sulit
                   // dibaca justru pada saat ia ditanyakan.
                   opacity={kept || lit ? 1 : 0.55}>
                  <circle
                    cx={at[0]} cy={at[1]}
                    r={(tone === "centre" ? 5 : kept ? 4 : 2.6) + (lit ? 1.5 : 0)}
                    fill={kept ? SCREEN_TONE[tone] : "none"}
                    stroke={SCREEN_TONE[tone]}
                    strokeWidth={tone === "centre" || lit ? 2 : 1.2}
                  />
                  {tone === "centre" && (
                    <circle
                      cx={at[0]} cy={at[1]} r={8.5}
                      fill="none" stroke={SCREEN_TONE.centre} strokeWidth={1}
                      strokeDasharray="2 2"
                    />
                  )}
                  {/* Nama sumur yang sedang ditunjuk digambar belakangan, di
                      atas seluruh peta; di sini ia akan tertimbun titik
                      tetangganya justru di gerombolan tempat ia paling
                      dibutuhkan. */}
                  {labelled.has(well.id) && !lit && (
                    <text x={at[0] + 7} y={at[1] + 3.5} style={wellLabel}>
                      {well.name}
                    </text>
                  )}
                  {/* Sasaran tunjuk lebih besar daripada titiknya: menuntut
                      ketepatan dua piksel dari tangan di atas tetikus membuat
                      keterangan yang bekerja terasa rusak. `<title>` berdiri
                      di sampingnya untuk yang menunggu tooltip bawaan
                      peramban — dan untuk yang membaca peta ini lewat
                      pembaca layar. */}
                  <circle
                    cx={at[0]} cy={at[1]} r={10} fill="transparent"
                    onPointerEnter={() => setHovered(well.id)}
                    onPointerLeave={() =>
                      setHovered((now) => (now === well.id ? null : now))}
                  >
                    <title>{wellTitle(well.name, tone, rows.get(well.id),
                                       screening.center_name)}</title>
                  </circle>
                </g>
              );
            })}

            {hot && (
              <g pointerEvents="none">
                <circle
                  cx={hot.at[0]} cy={hot.at[1]} r={11.5}
                  fill="none" stroke="var(--accent)" strokeWidth={1.3}
                />
                <text x={hot.at[0] + 9} y={hot.at[1] + 3.5} style={wellLabelLit}>
                  {hot.well.name}
                </text>
              </g>
            )}
          </svg>
        )}

        {/* Keterangan sumur yang ditunjuk: nama, keadaannya di dalam jaring,
            jaraknya dari pusat, dan tempatnya — yaitu angka yang sama dengan
            yang tertulis di tepi kisi, sehingga keduanya saling menerangkan.
            Ia mengapung di samping titiknya dan bukan di sudut panel: mata
            yang harus berpindah ke sudut kehilangan titik yang sedang
            ditunjuknya di tengah jalan. */}
        {hot && (
          <div
            data-well-readout={hot.well.id}
            style={{
              position: "absolute",
              // Dibalik ke kiri titik begitu titiknya duduk di separuh kanan
              // panel, supaya keterangannya tidak pernah terpotong tepi.
              ...(hot.at[0] > size.width / 2
                ? { right: Math.max(size.width - hot.at[0] + 13, 4) }
                : { left: hot.at[0] + 13 }),
              top: Math.min(Math.max(hot.at[1] - 30, 4), Math.max(height - 54, 4)),
              maxWidth: 190,
              display: "flex", flexDirection: "column", gap: 1,
              padding: "4px 7px",
              background: "var(--panel)",
              border: "1px solid var(--border-control)",
              borderRadius: "var(--radius)",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.18)",
              pointerEvents: "none", zIndex: 3,
            }}
          >
            <span style={{ font: "600 10px var(--font-mono)", color: "var(--ink)" }}>
              {hot.well.name}
            </span>
            <span style={{ font: "400 9px var(--font-sans)",
                           color: SCREEN_TONE[hot.tone] }}>
              {hot.tone}
              {hot.screened?.distance_m != null
                && ` · ${radiusText(hot.screened.distance_m)} from `
                   + `${screening.center_name}`}
            </span>
            <span style={{ font: "400 9px var(--font-mono)", color: "var(--ink-5)" }}>
              X {hot.well.x!.toFixed(0)} · Y {hot.well.y!.toFixed(0)}
            </span>
          </div>
        )}

        {located.length > 0 && (
          <MapZoomControls
            zoom={zoom.view.zoom}
            home={zoom.home}
            onIn={() => zoom.zoomBy(1.4)}
            onOut={() => zoom.zoomBy(1 / 1.4)}
            onReset={zoom.reset}
            // Sudut kiri atas sekarang milik angka northing paling utara, dan
            // tombol yang duduk di atasnya menutupi justru angka yang paling
            // sering dibaca orang — sama seperti di peta horizon model geologi.
            corner="top-right"
          />
        )}
      </div>

      <span style={hint}>
        {screening.kept} of {screening.asked} wells caught
        {beyond > 0 && ` · ${beyond} more outside this view`}
        {unplaced > 0 && ` · ${unplaced} without coordinates, not drawn`}
        {screening.mode === "trajectory"
          && " · the circle descends along the track; what is drawn is its head"}
      </span>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        {legend.map((tone) => (
          <span key={tone} style={{ ...hint, display: "flex", alignItems: "center",
                                    gap: 4 }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%",
                           background: SCREEN_TONE[tone] }} />
            {tone}
          </span>
        ))}
      </div>
    </div>
  );
}

const label: React.CSSProperties = {
  font: "600 9.5px var(--font-mono)", color: "var(--ink-5)",
  letterSpacing: "0.04em", textTransform: "uppercase",
};

const hint: React.CSSProperties = {
  font: "400 9.5px/1.6 var(--font-sans)", color: "var(--ink-6)",
};

const ringLabel: React.CSSProperties = {
  font: "600 9px var(--font-mono)", fill: "var(--accent)",
};

// Halo di belakang tulisannya, dan itu bukan hiasan: nama sumur kini melintasi
// garis kisi, dan aksara yang dipotong garis terbaca sebagai aksara yang lain.
const wellLabel: React.CSSProperties = {
  font: "400 9px var(--font-mono)", fill: "var(--ink-3)",
  paintOrder: "stroke", stroke: "var(--panel)", strokeWidth: 3,
  strokeLinejoin: "round",
};

const wellLabelLit: React.CSSProperties = {
  ...wellLabel, font: "600 9.5px var(--font-mono)", fill: "var(--accent)",
};

const tickLabel: React.CSSProperties = {
  font: "500 9px var(--font-mono)", fill: "var(--ink-5)",
  paintOrder: "stroke", stroke: "var(--panel)", strokeWidth: 3,
  strokeLinejoin: "round",
};
